import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Users, CheckCircle, XCircle, Package, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Vouchers',
      value: stats?.total_vouchers || 0,
      icon: Package,
      color: 'from-blue-500 to-cyan-400',
      testId: 'stat-total-vouchers'
    },
    {
      title: 'Available Vouchers',
      value: stats?.available_vouchers || 0,
      icon: FileText,
      color: 'from-emerald-500 to-teal-400',
      testId: 'stat-available-vouchers'
    },
    {
      title: 'Total Students',
      value: stats?.total_students || 0,
      icon: Users,
      color: 'from-violet-500 to-purple-400',
      testId: 'stat-total-students'
    },
    {
      title: 'Distributions',
      value: stats?.total_distributions || 0,
      icon: TrendingUp,
      color: 'from-orange-500 to-amber-400',
      testId: 'stat-distributions'
    },
    {
      title: 'Passed',
      value: stats?.passed_vouchers || 0,
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-400',
      testId: 'stat-passed'
    },
    {
      title: 'Failed',
      value: stats?.failed_vouchers || 0,
      icon: XCircle,
      color: 'from-red-500 to-rose-400',
      testId: 'stat-failed'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      <div>
        <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Dashboard Overview
        </h2>
        <p className="text-slate-600 dark:text-slate-400"  style={{ fontFamily: 'Inter, sans-serif' }}>
          Monitor your voucher distribution system at a glance
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover border-slate-200 dark:border-slate-700"  data-testid={stat.testId}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {stat.title}
                </CardTitle>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800 dark:text-slate-100" >{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 dark:border-slate-700" >
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Quick Actions
          </CardTitle>
          <CardDescription>
            Common tasks to manage your voucher system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/admin/vouchers')}
              data-testid="quick-action-upload-vouchers"
              className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 black:to-cyan-500/10 hover:from-blue-100 hover:to-cyan-100 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/30 rounded-xl border-2 border-blue-200 dark:border-blue-700 text-left transition-all"
            >
              <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3" />
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1">Upload Vouchers</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">Add new voucher codes to the system</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/students')}
              data-testid="quick-action-add-students"
              className="p-6 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 black:to-purple-500/10 hover:from-violet-100 hover:to-purple-100 dark:hover:from-violet-900/30 dark:hover:to-purple-900/30 rounded-xl border-2 border-violet-200 dark:border-violet-700 text-left transition-all"
            >
              <Users className="w-8 h-8 text-violet-600 dark:text-violet-400 mb-3" />
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1">Add Students</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">Register students to receive vouchers</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/distribute')}
              data-testid="quick-action-distribute"
              className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 black:to-teal-500/10 hover:from-emerald-100 hover:to-teal-100 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/30 rounded-xl border-2 border-emerald-200 dark:border-emerald-700 text-left transition-all"
            >
              <TrendingUp className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mb-3" />
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1">Distribute Vouchers</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">Assign vouchers to students</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/reports')}
              data-testid="quick-action-reports"
              className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 black:to-amber-500/10 hover:from-orange-100 hover:to-amber-100 dark:hover:from-orange-900/30 dark:hover:to-amber-900/30 rounded-xl border-2 border-orange-200 dark:border-orange-700 text-left transition-all"
            >
              <TrendingUp className="w-8 h-8 text-orange-600 dark:text-orange-400 mb-3" />
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1">View Reports</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">Export and analyze distribution data</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;